import { useEffect, useState, useMemo } from 'react';

const EMPTY = {
  idEmpleado: '',
  avatarUrl: 'http://',
  nickname: '',
  modenas: 0,
  monedasAcumuladas: 0,
  monedasGastadas: 0,
  notificacionesCursos: true,
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  empleados = [],
  personas = [],
  estudiantes = [],
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idEmpleado: '',
    avatarUrl: '',
    nickname: '',
  });

  const availableEmpleados = useMemo(() => {
    const usedIds = new Set(estudiantes.map(e => e.idEmpleado));
    if (mode === 'edit' && data) {
      usedIds.delete(data.idEmpleado);
    }

    return empleados
      .filter(e => !usedIds.has(e.idEmpleado))
      .map(e => {
        const p = personas.find(p => p.idPersona === e.idPersona);
        const nombre = p ? `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim() : `Empleado ${e.idEmpleado}`;
        return { idEmpleado: e.idEmpleado, nombre };
      }).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [empleados, personas, estudiantes, mode, data]);

  // ---- Helpers de validación ----
  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'idEmpleado':
        if (!value) error = 'El empleado es obligatorio.';
        break;
      case 'nickname':
        if (!value || !String(value).trim()) error = 'El nickname es obligatorio.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = Object.keys(form);
    let isValid = true;
    fields.forEach((f) => {
      if (!validateField(f, form[f])) {
        isValid = false;
      }
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleSubmit = () => {
    const isValid = validateAll();
    if (!isValid) return; // No se envía si hay errores
    onSubmit(form, mode);
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filledForm = {
        idEstudiante: data.idEstudiante ?? 0,
        idEmpleado: data.idEmpleado ?? '',
        avatarUrl: data.avatarUrl || 'http://',
        nickname: data.nickname ?? '',
        modenas: data.modenas ?? 0,
        monedasAcumuladas: data.monedasAcumuladas ?? 0,
        monedasGastadas: data.monedasGastadas ?? 0,
        notificacionesCursos: data.notificacionesCursos ?? false,
      };
      setForm(filledForm);

      // Validar datos precargados (opcional, para limpiar errores previos)
      setErrors({
        idEmpleado: '',
        avatarUrl: '',
        nickname: '',
      });
    } else {
      setForm(EMPTY);
      setErrors({
        idEmpleado: '',
        avatarUrl: '',
        nickname: '',
      });
    }
  }, [mode, data]);

  if (!open) return null;

  const allRequiredFilled =
    form.idEmpleado &&
    form.nickname;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = !allRequiredFilled || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo estudiante' : 'Editar estudiante'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empleado: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <select
          className={`infoField${errors.idEmpleado ? ' infoField--error' : ''}`}
          value={form.idEmpleado}
          onChange={(e) => handleChange('idEmpleado', Number(e.target.value) || '')}
        >
          <option value="">-- Selecciona un empleado --</option>
          {availableEmpleados.map(e => (
            <option key={e.idEmpleado} value={e.idEmpleado}>{e.nombre}</option>
          ))}
        </select>
        {errors.idEmpleado && <small style={errorStyle}>{errors.idEmpleado}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nickname: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.nickname ? ' infoField--error' : ''}`}
          value={form.nickname}
          onChange={(e) => handleChange('nickname', e.target.value)}
          maxLength={120}
        />
        {errors.nickname && <small style={errorStyle}>{errors.nickname}</small>}
      </div>



      <div style={{ display: 'flex', alignItems: 'center', marginTop: '1.5rem', gap: '1rem' }}>
        <label style={{ fontSize: '.95rem', color: '#1e293b', fontWeight: 600 }}>
          ¿Recibir Notificaciones?:
        </label>
        <div
          onClick={() => handleChange('notificacionesCursos', !form.notificacionesCursos)}
          style={{
            width: '42px',
            height: '22px',
            backgroundColor: form.notificacionesCursos ? '#10b981' : '#cbd5e1',
            borderRadius: '12px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            flexShrink: 0,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
          }}
          title={form.notificacionesCursos ? "Desactivar" : "Activar"}
        >
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '3px',
            left: form.notificacionesCursos ? '23px' : '3px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }} />
        </div>
        <span style={{ fontSize: '0.85rem', color: form.notificacionesCursos ? '#10b981' : '#64748b', fontWeight: 600 }}>
          {form.notificacionesCursos ? 'SÍ' : 'NO'}
        </span>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSubmit}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
